import { useEffect, useState } from "react";
import { extractErrorMessage, useStore } from "../../../../helpers";
import {
  extractProjectQuestionKeysFromSource,
  isEsIndexNotFoundError,
  useEsSampleDoc,
} from "../../tars-hooks/useEsSampleDoc";
import { resolveFacetAutogenerateSource } from "./resolve-facet-autogenerate-source";

export function useFacetAutogenerateSourceKeys(isOpen: boolean) {
  const { tarsStore, tarsAnalyticsStore } = useStore();
  const { mutateAsync } = useEsSampleDoc();
  const [keys, setKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [needsSourceSelection, setNeedsSourceSelection] = useState(false);
  const selectedSourceId = tarsAnalyticsStore.selectedSourceIds[0];

  useEffect(() => {
    if (!isOpen) return;

    const resolved = resolveFacetAutogenerateSource(
      (id) => tarsStore.getNode(id),
      selectedSourceId
    );
    if (!resolved.ok) {
      setKeys([]);
      setLoading(false);
      setFetchError(null);
      setNeedsSourceSelection(true);
      return;
    }

    setNeedsSourceSelection(false);
    let cancelled = false;
    setKeys([]);
    setFetchError(null);
    setLoading(true);

    void (async () => {
      try {
        const raw = await mutateAsync({ es_index: resolved.esIndex });
        if (cancelled) return;
        if (raw == null || typeof raw !== "object") {
          setFetchError("Sample document response was empty.");
          return;
        }
        const data = raw as Record<string, unknown>;
        const src = data._source ?? data.source;
        if (!src || typeof src !== "object") {
          setFetchError("No document fields in sample response.");
          return;
        }
        setKeys(extractProjectQuestionKeysFromSource(src as Record<string, unknown>).sort());
      } catch (err: unknown) {
        if (cancelled) return;
        setKeys([]);
        setFetchError(
          isEsIndexNotFoundError(err)
            ? "This index was not found in Elasticsearch. Survey data may not be indexed yet."
            : extractErrorMessage(err, "Could not load sample document.")
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedSourceId, mutateAsync, tarsStore]);

  return { keys, loading, fetchError, needsSourceSelection };
}
